/**
 * Shared carbon-intensity math + series assembly for:
 * - carbonIntensityService (Energy chart)
 * - populate_electricity_carbon_intensity.ts
 * - populate_ghg_inventory.ts (per-year resolution; prefers `electricity_carbon_intensity` per geofips when present)
 *
 * Keeps eGRID / LSE rules aligned: CAMX preference, retail_supplier, observation-anchored series
 * (interpolate between any LSE or eGRID observation; extrapolate only outside the observed span),
 * PCL tail carry, legacy eGRID-only fallback when no observations exist.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { CALIFORNIA_STATE_GEOFIPS } from './californiaGhgInventoryVmtService';
import {
  ELEC_COM_FORMAT,
  ELEC_RES_FORMAT,
  formatGeographyTypesForInventory,
  geofipsVariantsForQuery,
} from './energyConsumptionSources';
import {
  fillInteriorLinearGapsByYearInPlace,
  blendLosAngelesCountyCarbonIntensityGPerKwh,
  isLosAngelesCountyGeofips,
} from './losAngelesCountyGridCarbonIntensity';

export type CcasiForCi = {
  geofips?: string | number;
  egrid_match1?: string | null;
  egrid_match2?: string | null;
  egrid_match3?: string | null;
  pcl_match1?: string | null;
  pcl_match2?: string | null;
  pcl_match3?: string | null;
  load_serving_entity?: string | null;
};

export type CombinedCarbonIntensityPoint = {
  value: number;
  source: string;
  provider: string;
};

/** When eGRID returns multiple rows per utility×year (subregions), prefer CAMX for CA retail mix. */
export function observedGco2eForYearFromEgridRows(rows: any[], year: number): number | null {
  const forYear = rows.filter((r) => r && Number(r.year) === year);
  if (!forYear.length) return null;
  const camx = forYear.find((r) => String(r.subregion ?? '').trim().toUpperCase() === 'CAMX');
  const row = camx ?? forYear[0];
  const v = Number(row?.gco2e_per_kwh);
  return Number.isFinite(v) ? v : null;
}

/** Mean gCO₂e/kWh when `lse_carbon_intensity` has multiple rows per year. */
export function meanLseGco2eForYear(rows: any[], year: number): number | null {
  const vals = rows
    .filter((d) => Number(d.year) === year)
    .map((d) => Number(d.gco2e_per_kwh))
    .filter((x) => Number.isFinite(x));
  if (!vals.length) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

export function interpolateCiEgridSeries(years: number[], values: (number | null)[]): (number | null)[] {
  const interpolated: (number | null)[] = values.map((v) =>
    v !== null && v !== undefined ? v : null
  );
  fillInteriorLinearGapsByYearInPlace(years, interpolated);
  return interpolated;
}

function calculateLinearRegression(
  points: Array<{ year: number; value: number }>
): { slope: number; intercept: number } {
  if (points.length < 2) {
    return { slope: 0, intercept: points.length === 1 ? points[0].value : 0 };
  }
  const uniqueYears = new Set(points.map((p) => p.year));
  if (uniqueYears.size < 2) {
    return { slope: 0, intercept: points[0].value };
  }

  const n = points.length;
  const sumX = points.reduce((sum, p) => sum + p.year, 0);
  const sumY = points.reduce((sum, p) => sum + p.value, 0);
  const sumXY = points.reduce((sum, p) => sum + p.year * p.value, 0);
  const sumX2 = points.reduce((sum, p) => sum + p.year * p.year, 0);

  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;

  return { slope, intercept };
}

/**
 * Exterior nulls: constant carry from first/last **observed** eGRID anchor (matches Energy chart).
 * Rare interior nulls: short-window regression + clamp.
 */
export function extrapolateCiEgridSeries(
  years: number[],
  values: (number | null)[],
  originalObserved?: (number | null)[]
): (number | null)[] {
  const extrapolated = [...values];
  const obs = originalObserved ?? values;

  const observedPoints: Array<{ year: number; value: number }> = [];
  for (let i = 0; i < years.length; i++) {
    if (obs[i] !== null && obs[i] !== undefined) {
      observedPoints.push({ year: years[i], value: obs[i] as number });
    }
  }

  if (observedPoints.length === 0) {
    return extrapolated;
  }

  observedPoints.sort((a, b) => a.year - b.year);
  const minObs = Math.min(...observedPoints.map((p) => p.value));
  const maxObs = Math.max(...observedPoints.map((p) => p.value));
  const floorVal = Math.max(0, minObs * 0.65);
  const capVal = maxObs * 1.2;

  const firstWindow = observedPoints.slice(0, Math.min(5, observedPoints.length));
  const lastWindow = observedPoints.slice(-Math.min(5, observedPoints.length));
  const backwardReg = calculateLinearRegression(firstWindow);
  const forwardReg = calculateLinearRegression(lastWindow);

  const firstYearObs = observedPoints[0].year;
  const lastYearObs = observedPoints[observedPoints.length - 1].year;
  const firstObsIdx = years.indexOf(firstYearObs);
  const lastObsIdx = years.lastIndexOf(lastYearObs);
  const anchorFirst =
    firstObsIdx >= 0 && extrapolated[firstObsIdx] != null && Number.isFinite(Number(extrapolated[firstObsIdx]))
      ? Number(extrapolated[firstObsIdx])
      : observedPoints[0].value;
  const anchorLast =
    lastObsIdx >= 0 && extrapolated[lastObsIdx] != null && Number.isFinite(Number(extrapolated[lastObsIdx]))
      ? Number(extrapolated[lastObsIdx])
      : observedPoints[observedPoints.length - 1].value;

  for (let i = 0; i < years.length; i++) {
    if (extrapolated[i] !== null && extrapolated[i] !== undefined) {
      continue;
    }
    if (obs[i] !== null && obs[i] !== undefined) {
      continue;
    }

    const y = years[i];
    let projected: number;
    if (y < firstYearObs) {
      projected = anchorFirst;
    } else if (y > lastYearObs) {
      projected = anchorLast;
    } else {
      projected =
        y < (firstYearObs + lastYearObs) / 2
          ? backwardReg.slope * y + backwardReg.intercept
          : forwardReg.slope * y + forwardReg.intercept;
      projected = Math.max(0, projected);
      projected = Math.min(capVal, Math.max(floorVal, projected));
    }

    if (y < firstYearObs || y > lastYearObs) {
      projected = Math.max(0, projected);
    }
    extrapolated[i] = projected;
  }

  return extrapolated;
}

export function fillInteriorPositiveHolesInCombinedSeries(
  years: number[],
  combinedData: CombinedCarbonIntensityPoint[]
): void {
  for (let i = 0; i < years.length; i++) {
    const v = combinedData[i].value;
    if (Number.isFinite(v) && v > 0) continue;
    let pi = -1;
    let ni = -1;
    for (let j = i - 1; j >= 0; j--) {
      const w = combinedData[j].value;
      if (Number.isFinite(w) && w > 0) {
        pi = j;
        break;
      }
    }
    for (let j = i + 1; j < years.length; j++) {
      const w = combinedData[j].value;
      if (Number.isFinite(w) && w > 0) {
        ni = j;
        break;
      }
    }
    if (pi < 0 || ni < 0) continue;
    const y = years[i];
    const y0 = years[pi];
    const y1 = years[ni];
    if (y1 === y0) continue;
    const v0 = combinedData[pi].value;
    const v1 = combinedData[ni].value;
    const t = (y - y0) / (y1 - y0);
    const interpolated = v0 + t * (v1 - v0);
    if (!Number.isFinite(interpolated) || interpolated <= 0) continue;
    combinedData[i] = {
      value: interpolated,
      source: 'interpolated',
      provider: 'Interpolated',
    };
  }
}

export function applyPclTailCarryForExtrapolatedYears(
  years: number[],
  lseValues: (number | null)[],
  combinedData: CombinedCarbonIntensityPoint[],
  loadServingEntity: string | null | undefined
): void {
  const LSE_TAIL_MIN_YEAR = 2018;
  const MAX_YEAR_GAP_AFTER_LSE = 2;
  let lastLseIdx = -1;
  for (let i = 0; i < years.length; i++) {
    const v = lseValues[i];
    if (v != null && Number.isFinite(v) && v > 0) lastLseIdx = i;
  }
  if (lastLseIdx < 0 || years[lastLseIdx] < LSE_TAIL_MIN_YEAR) return;
  const anchorYear = years[lastLseIdx];
  const anchorVal = lseValues[lastLseIdx] as number;
  const entity = loadServingEntity || 'CCA';
  for (let i = lastLseIdx + 1; i < years.length; i++) {
    if (years[i] - anchorYear > MAX_YEAR_GAP_AFTER_LSE) break;
    if (combinedData[i].source !== 'extrapolated') continue;
    combinedData[i] = {
      value: anchorVal,
      source: 'extrapolated',
      provider: `${entity} - Power Content Label (carried forward; no ${years[i]} label in database)`,
    };
  }
}

function pclProviderLabel(loadServingEntity: string | null | undefined): string {
  return `${loadServingEntity || 'CCA'} - Power Content Label`;
}

function egridObservedProviderLabel(egridPrimaryName: string): string {
  return `${egridPrimaryName || 'eGRID Utility'} - EIA-861 & eGRID`;
}

/**
 * Legacy path when there is no raw LSE or eGRID observation in any year (rare).
 * Fills with eGRID interpolate/extrapolate and still prefers LSE in years where it appears
 * without a paired eGRID observation (should not occur if anchors empty, but kept for parity).
 */
function buildCombinedCarbonIntensityLegacyEgridFirstPass(
  years: number[],
  egridValues: (number | null)[],
  lseValues: (number | null)[],
  egridPrimaryName: string,
  loadServingEntity: string | null | undefined
): CombinedCarbonIntensityPoint[] {
  const egridInterpolated = interpolateCiEgridSeries(years, egridValues);
  const egridExtrapolated = extrapolateCiEgridSeries(years, egridInterpolated, egridValues);

  return years.map((year, yearIndex) => {
    const ccaValue = lseValues[yearIndex];
    if (ccaValue !== null && ccaValue !== undefined && Number.isFinite(ccaValue) && ccaValue > 0) {
      return {
        value: ccaValue,
        source: 'cca',
        provider: pclProviderLabel(loadServingEntity),
      };
    }

    const egridObservedValue = egridValues[yearIndex];
    if (
      egridObservedValue !== null &&
      egridObservedValue !== undefined &&
      Number.isFinite(egridObservedValue) &&
      egridObservedValue > 0
    ) {
      return {
        value: egridObservedValue,
        source: 'observed',
        provider: egridObservedProviderLabel(egridPrimaryName),
      };
    }

    const interpolatedValue = egridInterpolated[yearIndex];
    if (
      interpolatedValue !== null &&
      interpolatedValue !== undefined &&
      Number.isFinite(interpolatedValue) &&
      interpolatedValue > 0
    ) {
      return {
        value: interpolatedValue,
        source: 'interpolated',
        provider: 'Interpolated — between observed eGRID years',
      };
    }

    const extrapolatedValue = egridExtrapolated[yearIndex];
    const ev =
      extrapolatedValue != null && Number.isFinite(extrapolatedValue) && extrapolatedValue > 0
        ? extrapolatedValue
        : 0;
    return {
      value: ev,
      source: 'extrapolated',
      provider: 'Extrapolated',
    };
  });
}

function findNextObservedIndex(anchorValue: (number | null)[], fromExclusive: number): number {
  for (let j = fromExclusive + 1; j < anchorValue.length; j++) {
    if (anchorValue[j] != null && Number.isFinite(anchorValue[j]!) && anchorValue[j]! > 0) return j;
  }
  return -1;
}

function findPrevObservedIndex(anchorValue: (number | null)[], fromExclusive: number): number {
  for (let j = fromExclusive - 1; j >= 0; j--) {
    if (anchorValue[j] != null && Number.isFinite(anchorValue[j]!) && anchorValue[j]! > 0) return j;
  }
  return -1;
}

/**
 * First-pass combined series (before hole fill, PCL tail, LA county blend).
 * Uses **raw** LSE (PCL) and **raw** eGRID observations only as anchors. Gaps **between**
 * the first and last anchor year are filled with linear interpolation (any utility mix).
 * Years before the first anchor or after the last use linear extrapolation from the nearest
 * two anchors when available, otherwise constant carry from the edge anchor.
 * `source` uses 'cca' for chart compatibility; GHG scripts may map cca → their own label.
 */
export function buildCombinedCarbonIntensityPointsFirstPass(
  years: number[],
  egridValues: (number | null)[],
  lseValues: (number | null)[],
  egridPrimaryName: string,
  loadServingEntity: string | null | undefined
): CombinedCarbonIntensityPoint[] {
  const n = years.length;
  const anchorValue: (number | null)[] = Array(n).fill(null);
  const anchorIsPcl: (boolean | null)[] = Array(n).fill(null);

  for (let i = 0; i < n; i++) {
    const lv = lseValues[i];
    if (lv != null && Number.isFinite(lv) && lv > 0) {
      anchorValue[i] = lv;
      anchorIsPcl[i] = true;
      continue;
    }
    const ev = egridValues[i];
    if (ev != null && Number.isFinite(ev) && ev > 0) {
      anchorValue[i] = ev;
      anchorIsPcl[i] = false;
    }
  }

  let first = -1;
  let last = -1;
  for (let i = 0; i < n; i++) {
    if (anchorValue[i] != null) {
      if (first < 0) first = i;
      last = i;
    }
  }

  if (first < 0) {
    return buildCombinedCarbonIntensityLegacyEgridFirstPass(
      years,
      egridValues,
      lseValues,
      egridPrimaryName,
      loadServingEntity
    );
  }

  const anchorProviderAt = (i: number): string => {
    const v = anchorValue[i];
    if (v == null) return 'observed';
    return anchorIsPcl[i]
      ? pclProviderLabel(loadServingEntity)
      : egridObservedProviderLabel(egridPrimaryName);
  };

  const out: CombinedCarbonIntensityPoint[] = Array(n);

  for (let i = 0; i < n; i++) {
    if (anchorValue[i] != null) {
      const v = anchorValue[i]!;
      if (anchorIsPcl[i]) {
        out[i] = { value: v, source: 'cca', provider: pclProviderLabel(loadServingEntity) };
      } else {
        out[i] = { value: v, source: 'observed', provider: egridObservedProviderLabel(egridPrimaryName) };
      }
      continue;
    }

    const y = years[i];

    if (i < first) {
      const j0 = first;
      const j1 = findNextObservedIndex(anchorValue, first);
      const y0 = years[j0];
      const v0 = anchorValue[j0]!;
      if (j1 >= 0 && years[j1] !== y0) {
        const y1 = years[j1];
        const v1 = anchorValue[j1]!;
        const t = (y - y0) / (y1 - y0);
        const extrap = v0 + t * (v1 - v0);
        out[i] = {
          value: Math.max(0, extrap),
          source: 'extrapolated',
          provider: `Extrapolated — before ${y0} (trend from ${y0}–${y1})`,
        };
      } else {
        out[i] = {
          value: v0,
          source: 'extrapolated',
          provider: `Extrapolated — before first observed year (${y0})`,
        };
      }
      continue;
    }

    if (i > last) {
      const j0 = last;
      const j1 = findPrevObservedIndex(anchorValue, last);
      const y0 = years[j0];
      const v0 = anchorValue[j0]!;
      if (j1 >= 0 && years[j1] !== y0) {
        const y1 = years[j1];
        const v1 = anchorValue[j1]!;
        const t = (y - y0) / (y0 - y1);
        const extrap = v0 + t * (v0 - v1);
        out[i] = {
          value: Math.max(0, extrap),
          source: 'extrapolated',
          provider: `Extrapolated — after ${y0} (trend from ${y1}–${y0})`,
        };
      } else {
        out[i] = {
          value: v0,
          source: 'extrapolated',
          provider: `Extrapolated — after last observed year (${y0})`,
        };
      }
      continue;
    }

    const pi = findPrevObservedIndex(anchorValue, i);
    const ni = findNextObservedIndex(anchorValue, i);
    if (pi < 0 || ni < 0) {
      out[i] = { value: 0, source: 'extrapolated', provider: 'Extrapolated' };
      continue;
    }
    const y0 = years[pi];
    const y1 = years[ni];
    const v0 = anchorValue[pi]!;
    const v1 = anchorValue[ni]!;
    if (y1 === y0) {
      out[i] = { value: v0, source: 'interpolated', provider: 'Interpolated' };
      continue;
    }
    const t = (y - y0) / (y1 - y0);
    const interp = v0 + t * (v1 - v0);
    const p0 = anchorProviderAt(pi);
    const p1 = anchorProviderAt(ni);
    out[i] = {
      value: Math.max(0, interp),
      source: 'interpolated',
      provider: `Interpolated — between ${y0} (${p0}) and ${y1} (${p1})`,
    };
  }

  return out;
}

export function finalizeCombinedCarbonIntensitySeries(
  years: number[],
  lseValues: (number | null)[],
  combinedData: CombinedCarbonIntensityPoint[],
  loadServingEntity: string | null | undefined
): void {
  fillInteriorPositiveHolesInCombinedSeries(years, combinedData);
  applyPclTailCarryForExtrapolatedYears(years, lseValues, combinedData, loadServingEntity);
}

/**
 * Last-resort numeric fallback when no CCAS / eGRID / LSE / statewide row is available.
 * Prefer `electricity_carbon_intensity` geofips 6 (CAMX / eGRID summary) for CA counties & places — see
 * `tryCaliforniaStatewideElectricityCiFallback` below.
 */
const DEFAULT_GHG_ELECTRICITY_CI = 200;

/** True when inventory geofips is California statewide (electricity_carbon_intensity key = 6). */
export function isCaliforniaStateElectricityGeofips(geofips: number): boolean {
  return Number(geofips) === CALIFORNIA_STATE_GEOFIPS;
}

/**
 * CA county (6000–6999) or CA place (600000–699999) geofips used in inventory — not the state row (6).
 * Tracts / other FIPS patterns can be added if those rows appear in inventory.
 */
export function isCaliforniaElectricityInventoryGeofips(geofips: number): boolean {
  const g = Number(geofips);
  if (!Number.isFinite(g)) return false;
  if (g === CALIFORNIA_STATE_GEOFIPS) return true;
  if (g >= 6000 && g < 7000) return true;
  if (g >= 600000 && g < 700000) return true;
  return false;
}

/** California county FIPS as stored in inventory (6000–6999, excluding state key 6). */
export function isCaliforniaCountyInventoryGeofips(geofips: number): boolean {
  const g = Number(geofips);
  return Number.isFinite(g) && g >= 6000 && g < 7000 && g !== CALIFORNIA_STATE_GEOFIPS;
}

function measurementPriorityForDbElec(measurement: unknown): number {
  const s = String(measurement ?? '').toLowerCase();
  if (s.includes('observed')) return 3;
  if (s.includes('interpol')) return 2;
  if (s.includes('extrapol')) return 1;
  return 0;
}

function pickBestTotalKwhFromRows(
  rows: { total_kwh?: unknown; measurement?: unknown }[] | null | undefined
): number {
  if (!rows?.length) return 0;
  let best = 0;
  let bestP = -1;
  for (const r of rows) {
    const v = Number(r.total_kwh);
    if (!Number.isFinite(v) || v <= 0) continue;
    const p = measurementPriorityForDbElec(r.measurement);
    if (p > bestP || (p === bestP && v > best)) {
      bestP = p;
      best = v;
    }
  }
  return best;
}

/** Cities in a CA county from `population` (2023 rows), excluding the county geofips itself. */
export async function fetchCaliforniaCityGeofipsForCounty(
  client: SupabaseClient,
  countyGeofips: number
): Promise<number[]> {
  const { data: countyRow, error: cErr } = await client
    .from('population')
    .select('location_name')
    .eq('geofips', countyGeofips)
    .eq('geography_type', 'County')
    .maybeSingle();

  if (cErr || !countyRow?.location_name) return [];

  const countyName = String(countyRow.location_name);
  const normalizedCounty = countyName.toLowerCase().endsWith(' county')
    ? countyName
    : `${countyName} County`;
  const countyStem = normalizedCounty.replace(/\s+County\s*$/i, '').trim();

  const { data: cities, error } = await client
    .from('population')
    .select('geofips')
    .eq('geography_type', 'City')
    .ilike('county', `%${countyStem}%`)
    .eq('year', 2023);

  if (error || !cities?.length) return [];

  const uniq = new Set<number>();
  for (const r of cities as { geofips?: number | string }[]) {
    const g = Number(r.geofips);
    if (Number.isFinite(g) && g !== countyGeofips) uniq.add(g);
  }
  return Array.from(uniq);
}

async function fetchRetailElecKwhByPlaceForYear(
  client: SupabaseClient,
  placeGeofipsList: number[],
  year: number
): Promise<Map<number, number>> {
  const out = new Map<number, number>();
  if (!placeGeofipsList.length) return out;

  const variants: (number | string)[] = [];
  for (const g of placeGeofipsList) {
    for (const v of geofipsVariantsForQuery(g)) {
      variants.push(v);
    }
  }
  const cityGeoTypes = formatGeographyTypesForInventory('City');

  let { data: resRows } = await client
    .from(ELEC_RES_FORMAT)
    .select('geofips, total_kwh, measurement')
    .eq('year', year)
    .eq('sector', 'Residential')
    .in('geography_type', cityGeoTypes)
    .in('geofips', variants);

  if (!resRows?.length) {
    const r2 = await client
      .from(ELEC_RES_FORMAT)
      .select('geofips, total_kwh, measurement')
      .eq('year', year)
      .eq('sector', 'Residential')
      .in('geofips', variants);
    resRows = r2.data;
  }

  let { data: comRows } = await client
    .from(ELEC_COM_FORMAT)
    .select('geofips, total_kwh, measurement')
    .eq('year', year)
    .eq('sector', 'Commercial')
    .in('geography_type', cityGeoTypes)
    .in('geofips', variants);

  if (!comRows?.length) {
    const c2 = await client
      .from(ELEC_COM_FORMAT)
      .select('geofips, total_kwh, measurement')
      .eq('year', year)
      .eq('sector', 'Commercial')
      .in('geofips', variants);
    comRows = c2.data;
  }

  const resBy = new Map<number, { total_kwh?: unknown; measurement?: unknown }[]>();
  const comBy = new Map<number, { total_kwh?: unknown; measurement?: unknown }[]>();

  const add = (
    map: Map<number, { total_kwh?: unknown; measurement?: unknown }[]>,
    rows: { geofips?: unknown; total_kwh?: unknown; measurement?: unknown }[] | null
  ) => {
    for (const r of rows || []) {
      const g = Number(r.geofips);
      if (!Number.isFinite(g)) continue;
      if (!map.has(g)) map.set(g, []);
      map.get(g)!.push(r);
    }
  };

  add(resBy, resRows as any);
  add(comBy, comRows as any);

  for (const g of placeGeofipsList) {
    const kwh =
      pickBestTotalKwhFromRows(resBy.get(g)) + pickBestTotalKwhFromRows(comBy.get(g));
    if (kwh > 0) out.set(g, kwh);
  }
  return out;
}

async function fetchCcassRowsForGeofipsList(
  client: SupabaseClient,
  geofipsList: number[]
): Promise<Map<number, CcasiForCi>> {
  const map = new Map<number, CcasiForCi>();
  if (!geofipsList.length) return map;

  const chunkSize = 30;
  for (let i = 0; i < geofipsList.length; i += chunkSize) {
    const slice = geofipsList.slice(i, i + chunkSize);
    const orFilter = slice
      .flatMap((g) => [`geofips.eq.${g}`, `geofips.eq.${String(g)}`])
      .join(',');

    const { data, error } = await client
      .from('ccas')
      .select(
        'geofips, egrid_match1, egrid_match2, egrid_match3, pcl_match1, pcl_match2, pcl_match3, load_serving_entity'
      )
      .or(orFilter);

    if (error) continue;

    for (const r of data || []) {
      const g = Number((r as CcasiForCi).geofips);
      if (Number.isFinite(g)) map.set(g, r as CcasiForCi);
    }
  }
  return map;
}

/**
 * Option B: county electricity CI = Σ (kWh_place × CI_place) / Σ kWh_place for cities in the county
 * (residential + commercial kWh from elec_res_format / elec_com_format for that inventory year). Returns null if no cities
 * or zero total kWh — caller should use county `ccas` LSE/eGRID (Buildings parity) or
 * `electricity_carbon_intensity` geofips 6.
 */
export async function resolveCaliforniaCountyElectricityCiFromCitiesWeighted(
  client: SupabaseClient,
  countyGeofips: number,
  year: number,
  defaultIntensity: number = DEFAULT_GHG_ELECTRICITY_CI
): Promise<{ carbonIntensity: number; carbonIntensitySource: string; provider: string | null } | null> {
  if (!isCaliforniaCountyInventoryGeofips(countyGeofips)) return null;

  const cities = await fetchCaliforniaCityGeofipsForCounty(client, countyGeofips);
  if (!cities.length) return null;

  const kwhByPlace = await fetchRetailElecKwhByPlaceForYear(client, cities, year);
  const ccasByPlace = await fetchCcassRowsForGeofipsList(client, cities);

  let sumKwh = 0;
  let sumWeighted = 0;
  let nPlaces = 0;

  for (const placeG of cities) {
    const kwh = kwhByPlace.get(placeG) ?? 0;
    if (kwh <= 0) continue;

    const ccas = ccasByPlace.get(placeG) ?? null;
    const resolved = await resolveCarbonIntensityGPerKwhForInventoryYear(
      client,
      placeG,
      year,
      ccas,
      defaultIntensity
    );

    sumKwh += kwh;
    sumWeighted += kwh * resolved.carbonIntensity;
    nPlaces += 1;
  }

  if (sumKwh <= 0 || nPlaces === 0) return null;

  return {
    carbonIntensity: sumWeighted / sumKwh,
    carbonIntensitySource: 'county_kwh_weighted_cities',
    provider: `kWh-weighted (${nPlaces} cities; res+com elec_*_format)`,
  };
}

async function tryCaliforniaStatewideElectricityCiFallback(
  client: SupabaseClient,
  geofips: number,
  year: number
): Promise<{ carbonIntensity: number; provider: string | null } | null> {
  if (!isCaliforniaElectricityInventoryGeofips(geofips) || isCaliforniaStateElectricityGeofips(geofips)) {
    return null;
  }
  const row = await fetchElectricityCarbonIntensityGPerKwhForYearFromTable(client, year);
  if (!row) return null;
  const detail = row.detailSource ? ` — ${row.detailSource}` : '';
  return {
    carbonIntensity: row.value,
    provider: row.provider
      ? `California statewide (${row.provider})${detail}`
      : `California statewide (eGRID / CAMX)${detail}`,
  };
}

/**
 * Single-year g CO₂e/kWh from `electricity_carbon_intensity` for a geofips (tries numeric + string key).
 * Used for counties/cities with a populated row (same table as the Energy chart DB series).
 */
export async function fetchElectricityCarbonIntensityGPerKwhForGeofipsAndYear(
  client: SupabaseClient,
  geofips: number,
  year: number
): Promise<{ value: number; provider: string | null; detailSource: string | null } | null> {
  for (const gf of [geofips, String(geofips)] as const) {
    const { data, error } = await client
      .from('electricity_carbon_intensity')
      .select('gco2e_per_kwh, source, provider')
      .eq('geofips', gf as string | number)
      .eq('year', year)
      .maybeSingle();

    if (error) continue;
    const v = Number(data?.gco2e_per_kwh);
    if (data && Number.isFinite(v) && v > 0) {
      return {
        value: v,
        provider: data.provider != null ? String(data.provider) : null,
        detailSource: data.source != null ? String(data.source) : null,
      };
    }
  }
  return null;
}

/** California statewide (geofips 6) — thin wrapper around {@link fetchElectricityCarbonIntensityGPerKwhForGeofipsAndYear}. */
export async function fetchElectricityCarbonIntensityGPerKwhForYearFromTable(
  client: SupabaseClient,
  year: number
): Promise<{ value: number; provider: string | null; detailSource: string | null } | null> {
  return fetchElectricityCarbonIntensityGPerKwhForGeofipsAndYear(client, CALIFORNIA_STATE_GEOFIPS, year);
}

/**
 * Map year → g CO₂e/kWh for a place from `electricity_carbon_intensity` (live GHG / Policy baseline).
 */
export async function fetchElectricityCarbonIntensityMapForGeofips(
  client: SupabaseClient,
  geofips: number,
  yearMin: number,
  yearMax: number
): Promise<Map<number, number>> {
  const out = new Map<number, number>();
  const { data, error } = await client
    .from('electricity_carbon_intensity')
    .select('year, gco2e_per_kwh')
    .or(`geofips.eq.${geofips},geofips.eq.${String(geofips)}`)
    .gte('year', yearMin)
    .lte('year', yearMax);

  if (error || !data?.length) return out;

  for (const r of data as { year?: unknown; gco2e_per_kwh?: unknown }[]) {
    const y = Number(r.year);
    const v = Number(r.gco2e_per_kwh);
    if (Number.isFinite(y) && Number.isFinite(v) && v > 0) out.set(y, v);
  }
  return out;
}

function mapEciDbSourceToChartSource(
  source: string | null | undefined
): 'observed' | 'interpolated' | 'extrapolated' | 'cca' {
  const u = String(source || '').toLowerCase();
  if (u.includes('extrapol')) return 'extrapolated';
  if (u.includes('interpol')) return 'interpolated';
  return 'observed';
}

export type StateElectricityCiSeriesPoint = {
  year: number;
  value: number;
  chartSource: 'observed' | 'interpolated' | 'extrapolated' | 'cca';
  provider: string;
};

/**
 * Full series for CA State (geofips 6) from `electricity_carbon_intensity` for charting.
 */
export async function fetchCaliforniaStateElectricityCarbonIntensitySeriesFromTable(
  client: SupabaseClient,
  years: number[]
): Promise<StateElectricityCiSeriesPoint[]> {
  if (!years.length) return [];
  const minY = Math.min(...years);
  const maxY = Math.max(...years);
  const { data, error } = await client
    .from('electricity_carbon_intensity')
    .select('year, gco2e_per_kwh, source, provider')
    .eq('geofips', CALIFORNIA_STATE_GEOFIPS)
    .gte('year', minY)
    .lte('year', maxY)
    .order('year', { ascending: true });

  if (error || !data?.length) return [];

  return data
    .map((r: any) => {
      const y = Number(r.year);
      const value = Number(r.gco2e_per_kwh);
      const src = r.source != null ? String(r.source) : '';
      const prov = r.provider != null ? String(r.provider) : '';
      const provider =
        prov && src ? `${prov} (${src})` : prov || src || 'electricity_carbon_intensity';
      return {
        year: y,
        value,
        chartSource: mapEciDbSourceToChartSource(r.source),
        provider,
      };
    })
    .filter((r) => Number.isFinite(r.year) && Number.isFinite(r.value) && r.value > 0);
}

/**
 * One-year carbon intensity for GHG precalc:
 * CA state (geofips 6): `electricity_carbon_intensity` row for 6.
 * Other CA counties/places: `electricity_carbon_intensity` for that geofips if present; else Buildings order
 * (`ccas` → LSE → eGRID → LA blend); for counties only if still unresolved, kWh-weighted cities;
 * then geofips 6 statewide series; last resort numeric default.
 */
export async function resolveCarbonIntensityGPerKwhForInventoryYear(
  client: SupabaseClient,
  geofips: number,
  year: number,
  ccasData: CcasiForCi | null,
  defaultIntensity: number = DEFAULT_GHG_ELECTRICITY_CI
): Promise<{ carbonIntensity: number; carbonIntensitySource: string; provider: string | null }> {
  let carbonIntensity = defaultIntensity;
  let carbonIntensitySource = 'fallback';
  let provider: string | null = null;

  // California statewide (geofips 6): use preloaded `electricity_carbon_intensity` (CAMX / weighted state series).
  if (isCaliforniaStateElectricityGeofips(geofips)) {
    const row = await fetchElectricityCarbonIntensityGPerKwhForYearFromTable(client, year);
    if (row) {
      const detail = row.detailSource ? ` — ${row.detailSource}` : '';
      return {
        carbonIntensity: row.value,
        carbonIntensitySource: 'electricity_carbon_intensity',
        provider: row.provider ? `${row.provider}${detail}` : (row.detailSource || 'electricity_carbon_intensity'),
      };
    }
    return { carbonIntensity, carbonIntensitySource, provider };
  }

  // Prefer `electricity_carbon_intensity` row for this geofips+year when present (populate script / CAMX per place).
  if (isCaliforniaElectricityInventoryGeofips(geofips) && !isCaliforniaStateElectricityGeofips(geofips)) {
    const ec = await fetchElectricityCarbonIntensityGPerKwhForGeofipsAndYear(client, geofips, year);
    if (ec) {
      const detail = ec.detailSource ? ` — ${ec.detailSource}` : '';
      return {
        carbonIntensity: ec.value,
        carbonIntensitySource: 'electricity_carbon_intensity',
        provider: ec.provider ? `${ec.provider}${detail}` : ec.detailSource || 'electricity_carbon_intensity',
      };
    }
  }

  // Buildings & Energy page order: when `ccas` exists, LSE → eGRID → LA blend for this geofips first.
  if (ccasData) {
    const ccaMatches = [ccasData.pcl_match1, ccasData.pcl_match2, ccasData.pcl_match3].filter(Boolean) as string[];

    if (ccaMatches.length > 0) {
      const { data: lseRows, error: lseErr } = await client
        .from('lse_carbon_intensity')
        .select('year, retail_supplier, gco2e_per_kwh')
        .eq('year', year)
        .in('retail_supplier', ccaMatches);

      if (!lseErr && lseRows?.length) {
        const mean = meanLseGco2eForYear(lseRows, year);
        if (mean != null && mean > 0) {
          carbonIntensity = mean;
          carbonIntensitySource = 'cca';
          provider = ccasData.load_serving_entity || null;
        }
      }
    }

    if (carbonIntensitySource === 'fallback') {
      const egridMatches = [ccasData.egrid_match1, ccasData.egrid_match2, ccasData.egrid_match3].filter(
        Boolean
      ) as string[];

      if (egridMatches.length > 0) {
        const { data: egridRows, error: egridErr } = await client
          .from('egrid_utilities')
          .select('year, utility_name, gco2e_per_kwh, subregion')
          .eq('year', year)
          .in('utility_name', egridMatches);

        if (!egridErr && egridRows?.length) {
          const v = observedGco2eForYearFromEgridRows(egridRows, year);
          if (v != null && v > 0) {
            carbonIntensity = v;
            carbonIntensitySource = 'egrid_observed';
            provider = egridMatches[0] || null;
          }
        }
      }
    }

    if (isLosAngelesCountyGeofips(geofips)) {
      const blended = await blendLosAngelesCountyCarbonIntensityGPerKwh(
        year,
        carbonIntensity,
        [year],
        client,
        provider
      );
      if (blended) {
        carbonIntensity = blended.value;
        carbonIntensitySource = 'la_county_weighted';
        provider = blended.provider;
      }
    }
  }

  // California county: kWh-weighted cities only if LSE/eGRID did not resolve (fills gaps / no utility row for year).
  if (isCaliforniaCountyInventoryGeofips(geofips)) {
    if (carbonIntensitySource === 'fallback') {
      const cw = await resolveCaliforniaCountyElectricityCiFromCitiesWeighted(
        client,
        geofips,
        year,
        defaultIntensity
      );
      if (cw) return cw;
    }
  } else if (carbonIntensitySource !== 'fallback') {
    return { carbonIntensity, carbonIntensitySource, provider };
  }

  if (!ccasData) {
    const st = await tryCaliforniaStatewideElectricityCiFallback(client, geofips, year);
    if (st) {
      return {
        carbonIntensity: st.carbonIntensity,
        carbonIntensitySource: 'electricity_carbon_intensity',
        provider: st.provider,
      };
    }
    return { carbonIntensity, carbonIntensitySource, provider };
  }

  if (carbonIntensitySource === 'fallback') {
    const st = await tryCaliforniaStatewideElectricityCiFallback(client, geofips, year);
    if (st) {
      return {
        carbonIntensity: st.carbonIntensity,
        carbonIntensitySource: 'electricity_carbon_intensity',
        provider: st.provider,
      };
    }
  }

  return { carbonIntensity, carbonIntensitySource, provider };
}
